import { all, call, put, takeLatest } from 'redux-saga/effects';
import api from '../../helpers/api';
import { SWITCH_CONTEXT, REFRESH_ACTIVE_CONTEXT } from './actionTypes';
import {
  switchContext,
  switchContextSuccess,
  switchContextFail,
  refreshActiveContextSuccess,
  refreshActiveContextFail,
} from './actions';

/**
 * POST /auth/switch-context. On success persist the reissued JWT and the new
 * active-context metadata into localStorage so guards (AuthProtected, axios
 * interceptor) and a fresh page-reload all see the new context. Then invoke
 * the caller's navigation callback with the destination path.
 *
 * <p>Body shape: {@code { organizationId: number | null }}. Pass {@code null}
 * to switch to Global View — only allowed when the user has
 * {@code org:aggregate-view} permission in two or more organizations.
 */
function* switchContextSaga({ payload: { organizationId, onSuccess } }) {
  try {
    const { data } = yield call(api.post, 'auth/switch-context', { organizationId });
    const payload = data?.response;

    if (!payload?.token) {
      throw new Error('Invalid switch-context response');
    }

    // Persist the new token (axios interceptor reads it on every request).
    localStorage.setItem('token', payload.token);

    // Mirror the active context into authUser so a refresh hydrates correctly.
    const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
    authUser.activeOrganizationId = payload.activeOrganizationId ?? null;
    authUser.activeContextName = payload.activeContextName ?? null;
    authUser.activeOrganizationCurrency = payload.activeOrganizationCurrency ?? null;
    authUser.activeOrganizationDefaultWorkOrderView = payload.activeOrganizationDefaultWorkOrderView ?? null;
    authUser.isGlobalView = !!payload.globalView || payload.activeOrganizationId == null;
    if (typeof payload.canEnterGlobalView === 'boolean') {
      authUser.canEnterGlobalView = payload.canEnterGlobalView;
    }
    if (Array.isArray(payload.roles)) authUser.roles = payload.roles;
    if (payload.permissions) {
      authUser.permissions = Array.isArray(payload.permissions)
        ? payload.permissions
        : Array.from(payload.permissions);
    }
    // Team-scoped asset visibility is per-organization, so it is re-stamped on every switch.
    authUser.visibilityRestricted = Boolean(payload.visibilityRestricted);
    authUser.assetTypeScope = payload.assetTypeScope ?? null;
    // Clear deprecated legacy fields on every switch so stale values don't bleed
    // into the post-switch session.
    delete authUser.activeContextType;
    delete authUser.activeContextId;
    delete authUser.accessibleSubOrgIds;
    delete authUser.globalMemberships;
    localStorage.setItem('authUser', JSON.stringify(authUser));

    // Nudge usePermission() to re-read so permission-gated routes/buttons update.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('permissions-updated'));
      // Lists hold their rows in page-local state, so they need a separate nudge to refetch:
      // the org switch changes WHICH assets are visible, not just which buttons are enabled.
      window.dispatchEvent(new Event('visibility-scope-changed'));
    }

    yield put(switchContextSuccess(payload));

    if (typeof onSuccess === 'function') {
      let target;
      if (payload.activeOrganizationId == null) {
        target = '/app/global/dashboard';
      } else {
        // Match Login.js role-based landing: requestors land on service requests,
        // everyone else on the reactive WO list. The web portal has no /dashboard
        // route — using it would white-screen the user.
        const role = authUser?.role || (Array.isArray(authUser?.roles) ? authUser.roles[0] : null);
        target = role === 'ROLE_REQUESTOR' ? '/service-requests' : '/reactive';
      }
      onSuccess(target);
    }
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to switch context';
    yield put(switchContextFail(message));
  }
}

// Throttle repeat refresh fires across triggers (focus, switcher-open, etc.)
// so the backend isn't hammered if the user rapidly tabs in/out or toggles
// the dropdown. Kept in module scope — one refresh per tab per window.
const REFRESH_MIN_INTERVAL_MS = 5000;
let lastRefreshAt = 0;

/**
 * GET /auth/active-context. Pulls memberships + canEnterGlobalView + active
 * org fresh from the backend so the slice can recover from stale state
 * (newly granted org, revoked org:aggregate-view, etc.) without a re-login.
 *
 * <p>Two recovery branches:
 * <ul>
 *   <li>User is in Global View locally but the backend says they no longer
 *       qualify (canEnterGlobalView=false): auto-switch them to the first
 *       remaining membership so they aren't trapped on the global dashboard
 *       by AuthProtected's redirect guard.</li>
 *   <li>User is in Global View, qualifies, but the JWT's active org doesn't
 *       match: trust the JWT (we don't rotate it here).</li>
 * </ul>
 */
function* refreshActiveContextSaga() {
  const now = Date.now();
  if (now - lastRefreshAt < REFRESH_MIN_INTERVAL_MS) {
    return;
  }
  lastRefreshAt = now;

  // Skip the call entirely if the user isn't logged in — refresh triggers
  // (window focus) fire on the login page too.
  if (typeof window !== 'undefined' && !localStorage.getItem('token')) {
    return;
  }

  try {
    const { data } = yield call(api.get, 'auth/active-context');
    const payload = data?.response;
    if (!payload) {
      throw new Error('Invalid active-context response');
    }

    yield put(refreshActiveContextSuccess(payload));

    // Auto-recovery: if the user is on Global-View pages but no longer
    // qualifies, drop them into the first available org so they aren't
    // stuck on /app/global/dashboard (AuthProtected redirects every
    // org-scoped route back to it while isGlobalView=true).
    const isLocallyGlobal = payload.activeOrganizationId == null;
    if (isLocallyGlobal && !payload.canEnterGlobalView) {
      const memberships = Array.isArray(payload.memberships) ? payload.memberships : [];
      const fallbackOrgId = memberships[0]?.organizationId ?? null;
      if (fallbackOrgId != null) {
        yield put(
          switchContext(fallbackOrgId, (target) => {
            if (typeof window !== 'undefined') {
              window.location.href = target;
            }
          })
        );
      }
    }
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to refresh active context';
    yield put(refreshActiveContextFail(message));
  }
}

export default function* activeContextSaga() {
  yield all([
    takeLatest(SWITCH_CONTEXT, switchContextSaga),
    takeLatest(REFRESH_ACTIVE_CONTEXT, refreshActiveContextSaga),
  ]);
}
